import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useAppState, type AppSnapshot } from '../../app/state';
import { callAction, nodesUsingDoc } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { DocPill } from '../../components/DocPill';
import {
  DOC_BEAN_COLOR,
  DOC_ID_FIELD_BY_KEY,
  DOC_KINDS,
  PIPELINE_COLOR,
  PIPELINE_LABEL,
  docUrlFor,
} from '../../lib/docs';
import { canDocBtn, canEditAllDocs, canEditMap, canEnterEdit } from '../../lib/permissions';
import { OWED_CUSTOM_LABEL, OWED_PILL_LABEL, plannedMissing } from '../../lib/health';
import { canAssign } from '../../lib/responsibility';
import type { DocId, DocPerm, DocPlan, DocType, MapNode, NodeId } from '../../lib/types';

/**
 * The documentation section of the node popup — `#pp-docs` in both of its shapes.
 *
 * VIEW MODE lists only the slots that are filled AND that this group may see: a pill per
 * documentation page, opening it in the read-only viewer. EDIT MODE lists all four fixed slots
 * whether filled or not — because the point of the edit shape is to attach one — plus the
 * unbounded custom list, and opening a page from here opens it for EDITING.
 *
 * THE PILLS ARE `DocPill`. `fillDocPill()` — the vanilla builder these four call sites were the
 * last users of — is deleted with this file's arrival, and with it `docIconEl()`,
 * `setDocPillLabel()` and `svgIcon()`. That was the whole reason `DocPill` moved ahead of the
 * region that needed it (see its own header): one pill format, one implementation, across a
 * surface that straddled both sides of the migration.
 *
 * THE ASYNC RELABEL IS KEPT, AND KEPT LOCAL. `docTitles` comes from `/api/map` and can be
 * missing or stale for a page created in another tab, so each attached pill asks the server for
 * its page and relabels itself. The answer is deliberately NOT written back into `docTitles`:
 * that map is also what the map's own doc beans read, and pushing a title into it would relabel
 * surfaces the original never touched.
 */

/** The server's title for one page, or null until it answers. `onFail` is shown if it does not. */
function useServerDocTitle(docId: DocId | null | undefined, locale: string, onFail?: string) {
  const [title, setTitle] = useState<string | null>(null);
  useEffect(() => {
    if (!docId) return;
    let live = true;
    void apiRequest<{ title: string }>(`/api/docs/${docId}`, { query: { lang: locale } }).then(
      (d) => {
        if (live) setTitle(d.title);
      },
      () => {
        if (live && onFail !== undefined) setTitle(onFail);
      },
    );
    return () => {
      live = false;
    };
  }, [docId, locale, onFail]);
  return title;
}

/* ---- the workflow dot (V0.38) ------------------------------------------------------ *
 *                                                                                      *
 * A STATE INDICATOR AND NOTHING ELSE. Every transition in this app happens in ONE       *
 * place, the doc bar's `WorkflowControl`, and a second way to approve a page would be a *
 * second workflow to keep in agreement — the failure this codebase has already paid for *
 * once. So this is a coloured dot with a tooltip, not a menu.                           *
 *                                                                                      *
 * It reads `s.docWorkflow`. The server resolves the locale fallback into the entries    *
 * now, so "this page has no version in the language being shown" arrives as             *
 * `fallback: true` — and the dot skips those, keeping its old meaning exactly: it only  *
 * ever describes the translation the current language would actually show. Nothing is   *
 * drawn for a reader either — the endpoint that fills the map is participant-gated, and *
 * the explicit gate is what says that is intended rather than incidental.               *
 * ----------------------------------------------------------------------------------- */

function workflowOf(docId: DocId | null | undefined, s: AppSnapshot) {
  // Participants (V0.37): the endpoint that fills the map is participant-gated, so this is
  // the same population that has data — the explicit gate says intended, not incidental.
  if (!docId || !canEnterEdit(s.perm) || !s.docWorkflowLoaded) return null;
  const entry = s.docWorkflow[docId] ?? null;
  return entry && !entry.fallback ? entry : null;
}

/**
 * A pill with its review dot laid over it.
 *
 * THE DOT IS A SIBLING OF THE PILL, not a child, because `DocPill` is the one pill
 * implementation in the app and every pixel baseline photographs it — the map's beans and the
 * doc-nav band would inherit anything added inside it.
 *
 * THE WRAPPER IS ALWAYS RENDERED, dot or no dot, and it is `display:flex`. Both halves of that
 * are load-bearing rather than tidy. Always, so the layout does not move depending on whether
 * a page happens to have a review state; flex, because `.docpill` is `inline-flex` and a
 * `display:block` wrapper would put it in an inline formatting context — a line box, with the
 * descender gap under it — where in `#pp-docs`'s flex column it is a blockified flex item
 * today. A flex wrapper keeps it one, so the box is exactly the box it was.
 */
function PillWithReview({
  docId,
  s,
  style,
  children,
}: {
  docId: DocId | null | undefined;
  s: AppSnapshot;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const entry = workflowOf(docId, s);
  const when = entry?.reviewedAt ? ` — ${new Date(entry.reviewedAt).toLocaleDateString()}` : '';
  return (
    <span className={entry ? 'docpill-review has-dot' : 'docpill-review'} style={style}>
      {children}
      {entry ? (
        <span
          className="docpill-reviewdot"
          style={{ background: PIPELINE_COLOR[entry.workflowState] }}
          title={`Workflow: ${PIPELINE_LABEL[entry.workflowState]}${when}`}
        />
      ) : null}
    </span>
  );
}

export interface PopupDocsProps {
  node: MapNode;
  s: AppSnapshot;
  readOnly: boolean;
  /** The UNSAVED documentation plan, present only when its editor was drawn (V0.43.2).
   *  Wrapped, so "not drawn for this viewer" and "no plan" stay two different sentences. */
  docPlan?: { plan: DocPlan | null };
}

export function PopupDocs({ node, s, readOnly, docPlan }: PopupDocsProps) {
  return (
    <div id="pp-docs">
      {readOnly ? <ReadDocs node={node} s={s} /> : <EditDocs node={node} s={s} docPlan={docPlan} />}
    </div>
  );
}

/* ---- view mode ------------------------------------------------------------------- */

function ReadDocs({ node, s }: { node: MapNode; s: AppSnapshot }) {
  // A slot shows only when its URL is set AND the group may see that button.
  const avail = DOC_KINDS.filter(
    (k) => canDocBtn(s.perm, k.perm) && (node[k.key] || '').trim(),
  );
  const customAvail = canDocBtn(s.perm, 'custom') ? node.customDocs || [] : [];

  // NO OWED PILLS HERE (V0.43.2). What a node still owes is an EDIT-CONTEXT fact on every
  // surface now: the map draws it in edit mode or under the heatmap, the band above a document
  // behind that page's own edit switch, and this panel in its edit shape. The read-only shape
  // used to be the one exception, showing them to anyone who could create — which meant the
  // same node announced its debt in one place and stayed silent in the other two.
  if (!avail.length && !customAvail.length) {
    return <span style={{ fontSize: 13, color: 'var(--ink-faint)' }}>No documentation available.</span>;
  }

  return (
    <>
      {avail.map((k) => {
        const url = (node[k.key] || '').trim();
        const docId = (node[DOC_ID_FIELD_BY_KEY[k.key]] as DocId | null) ?? null;
        return (
          <PillWithReview key={k.key} docId={docId} s={s}>
            <ReadPill
              docId={docId}
              // The kind's name, which is what the map and the band fall back to. `k.open`
              // ("Open documentation") stays where a verb phrase belongs — the tooltip.
              fallback={s.docTitles[docId ?? ''] || k.label}
              color={DOC_BEAN_COLOR[k.key]}
              docTypes={s.docTypes}
              locale={s.currentLocale}
              // From the seat (V0.47): the node leaves the popup for the page's bubble; the
              // popup stays open underneath, as `openDoc()` always left it.
              onClick={() =>
                callAction('openDocFromNode', node.id, { docId, url, title: `${node.label} — ${k.label}` }, 'current')
              }
            />
          </PillWithReview>
        );
      })}
      {customAvail.map((docId) => {
        const title = s.docTitles[docId] || 'Untitled';
        return (
          <PillWithReview key={docId} docId={docId} s={s}>
            <DocPill
              variant="block"
              label={title}
              color="var(--ink-faint)"
              docId={docId}
              docTypes={s.docTypes}
              onClick={() =>
                callAction(
                  'openDocFromNode',
                  node.id,
                  {
                    docId,
                    url: docUrlFor(docId, { docUrls: s.docUrls, locale: s.currentLocale }),
                    title: `${node.label} — ${title}`,
                  },
                  'current',
                )
              }
            />
          </PillWithReview>
        );
      })}
    </>
  );
}

/** One view-mode pill, relabelled if the server knows a better title than `/api/map` did. */
function ReadPill({
  docId,
  fallback,
  color,
  docTypes,
  locale,
  onClick,
}: {
  docId: DocId | null;
  fallback: string;
  color: string;
  docTypes: Record<DocId, DocType>;
  locale: string;
  onClick: () => void;
}) {
  // No `onFail`: a failed lookup keeps the generic "Open documentation" wording, as it did.
  const fetched = useServerDocTitle(docId, locale);
  return (
    <DocPill
      variant="block"
      label={fetched ?? fallback}
      color={color}
      docId={docId}
      docTypes={docTypes}
      onClick={onClick}
    />
  );
}

/**
 * EDIT MODE — one row per DOCUMENT, and the plan decides which rows exist (V0.43.2).
 *
 * It used to be one block per SLOT: a heading, then the pill or a sentence about its absence,
 * then a button underneath. Five blocks whether or not the node had anything to do with them.
 * Now a row exists for a document that is ATTACHED, or for one the plan OWES, and for nothing
 * else — the heading went with it, because a pill already names its document and an owed pill
 * names its kind.
 *
 * REACTIVE TO THE DRAFT. `docPlan` is the plan editor's unsaved state, threaded down from
 * `PopupBody`. Ticking Course adds its row immediately, before any save, because `owed` is
 * computed from the draft rather than from `node.expectedDocs`. The wrapper distinguishes "the
 * plan editor was not drawn for this viewer" (absent) from "there is no plan" (`{plan: null}`),
 * which is the same distinction `PopupDraft.docPlan` makes on the way out.
 *
 * The map behind the popup still reads the STORED plan, so an unsaved tick shows here and not
 * there. That is deliberate: the popup is the editor's view, the map is the record — and it is
 * why pressing an owed pill records the plan before creating the page.
 */
function EditDocs({
  node,
  s,
  docPlan,
}: {
  node: MapNode;
  s: AppSnapshot;
  docPlan?: { plan: DocPlan | null };
}) {
  const customDocs = node.customDocs || [];
  const canCreate = canEditMap(s.perm) || canAssign(s, node.id);
  const facts = docPlan ? ({ ...node, expectedDocs: docPlan.plan } as MapNode) : node;
  const owed = canCreate
    ? plannedMissing(facts, (perm) => canDocBtn(s.perm, perm))
    : { kinds: [], customMissing: 0 };
  const attached = DOC_KINDS.filter((k) => !!(node[k.key] || '').trim());

  // NO "nothing here yet" LINE, deliberately. V0.45 briefly added one and it was removed the
  // same day: the plan hint above already says a node with no plan promises nothing and tells
  // you to tick a kind, so a second sentence underneath restates it in different words. One
  // message about one fact.
  return (
    <>
      {/* ATTACHED FIRST, then owed — the order the map's rows already use. */}
      {attached.map((k) => {
        const docId = (node[DOC_ID_FIELD_BY_KEY[k.key]] as DocId | null) ?? null;
        return (
          <DocRow
            key={k.key}
            nodeId={node.id}
            docId={docId}
            slotKey={k.key}
            title={s.docTitles[docId ?? ''] || k.label}
            pill={
              <PillWithReview docId={docId} s={s} style={{ flex: 1, minWidth: 0 }}>
                <SlotPill
                  docId={docId}
                  fallback={s.docTitles[docId ?? ''] || k.label}
                  color={DOC_BEAN_COLOR[k.key]}
                  docTypes={s.docTypes}
                  locale={s.currentLocale}
                  onClick={() => {
                    if (docId) callAction('openDocFromNode', node.id, { docId }, 'edit');
                  }}
                />
              </PillWithReview>
            }
          />
        );
      })}

      {/* Custom docs: an unbounded, freeform-titled list distinct from the four fixed slots.
          The picker APPENDS to `customDocs` rather than overwriting a field, so a custom row
          is detached by dropping it from that list, not by clearing a slot. */}
      {customDocs.map((docId) => (
        <DocRow
          key={docId}
          nodeId={node.id}
          docId={docId}
          slotKey="custom"
          title={s.docTitles[docId] || 'Untitled'}
          pill={
            <PillWithReview docId={docId} s={s} style={{ flex: 1, minWidth: 0 }}>
              <DocPill
                variant="block"
                label={s.docTitles[docId] || docId}
                color="var(--ink-faint)"
                docId={docId}
                docTypes={s.docTypes}
                title="Click to edit this document"
                style={{ flex: 1, width: 'auto' }}
                onClick={() => callAction('openDocFromNode', node.id, { docId }, 'edit')}
              />
            </PillWithReview>
          }
        />
      ))}

      {/* OWED: the same dotted pill the map and the band draw, with the same click. */}
      {owed.kinds.map((perm) => {
        const k = DOC_KINDS.find((d) => d.perm === perm)!;
        return (
          <OwedRow
            key={`owed-${perm}`}
            nodeId={node.id}
            perm={perm}
            slotKey={k.key}
            label={OWED_PILL_LABEL(k.label)}
            color={DOC_BEAN_COLOR[k.key]}
            hint={`Planned — click to create the ${k.label} page`}
            docPlan={docPlan}
          />
        );
      })}
      {Array.from({ length: owed.customMissing }, (_, i) => (
        <OwedRow
          key={`owed-custom-${i}`}
          nodeId={node.id}
          perm="custom"
          slotKey="custom"
          label={OWED_PILL_LABEL(OWED_CUSTOM_LABEL)}
          color="var(--ink-faint)"
          hint="Planned — click to create this custom document"
          docPlan={docPlan}
        />
      ))}
    </>
  );
}

/**
 * An attached document's row: the pill, then what can be done to it.
 *
 * REMOVE ASKS IN THE ROW, the picker's precedent — what you are about to remove stays on
 * screen and the list behind it is never covered. Two outcomes, because they are genuinely
 * different acts and the old UI only offered the first, buried inside the picker: detach it
 * from THIS node, or delete the page everywhere. The second names how many nodes lose it, and
 * is drawn only for those the server would allow (`EDIT_MAP` or `EDIT_ALL_DOCS`).
 */
function DocRow({
  nodeId,
  docId,
  slotKey,
  title,
  pill,
}: {
  nodeId: NodeId;
  docId: DocId | null;
  slotKey: string;
  title: string;
  pill: ReactNode;
}) {
  const s = useAppState();
  const [asking, setAsking] = useState(false);
  const mayDelete = canEditMap(s.perm) || canEditAllDocs(s.perm);
  const used = docId ? nodesUsingDoc(docId) : 0;

  const detach = () => {
    if (slotKey === 'custom') {
      if (docId) callAction('removeCustomDoc', nodeId, docId);
    } else {
      callAction('detachDocFromTarget', nodeId, slotKey);
    }
    setAsking(false);
  };

  if (asking) {
    return (
      <div className="pp-doc-field pp-doc-asking">
        <span className="pp-doc-asktext">
          Remove “{title}”?{' '}
          {used > 1 ? `${used} nodes use this page.` : 'Only this node uses it.'}
        </span>
        <button className="pbtn" onClick={() => setAsking(false)}>
          Cancel
        </button>
        <button className="pbtn" onClick={detach}>
          From this node
        </button>
        {mayDelete && docId ? (
          <button
            className="pbtn pp-doc-delete"
            onClick={() => {
              void (callAction('deleteDocEverywhere', docId) as Promise<boolean>);
              setAsking(false);
            }}
          >
            Delete everywhere
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="pp-doc-field">
      {pill}
      <button className="pbtn" onClick={() => callAction('openDocPicker', nodeId, slotKey)}>
        Select existing
      </button>
      <button className="pbtn" onClick={() => setAsking(true)}>
        Remove
      </button>
    </div>
  );
}

/**
 * An owed document's row: the dotted pill that creates it, and the button that attaches one
 * that already exists instead.
 *
 * The pill goes inert while its create is in flight. Two fast clicks used to send two POSTs,
 * the second of which 409s on a filled slot and surfaces as "Could not create the document" —
 * and awaiting the plan write first would have widened that window to a round trip.
 */
function OwedRow({
  nodeId,
  perm,
  slotKey,
  label,
  color,
  hint,
  docPlan,
}: {
  nodeId: NodeId;
  perm: DocPerm;
  slotKey: string;
  label: string;
  color: string;
  hint: string;
  docPlan?: { plan: DocPlan | null };
}) {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  return (
    <div className="pp-doc-field">
      <DocPill
        variant="block"
        planned
        label={label}
        color={color}
        title={hint}
        disabled={busy}
        style={{ flex: 1, width: 'auto', minWidth: 0 }}
        onClick={() => {
          setBusy(true);
          void (
            callAction('createPlannedDocForNode', nodeId, perm, docPlan) as Promise<void>
          ).finally(() => {
            // The row usually unmounts on success — the slot fills, so nothing owes it any more.
            if (alive.current) setBusy(false);
          });
        }}
      />
      <button className="pbtn" onClick={() => callAction('openDocPicker', nodeId, slotKey)}>
        Select existing
      </button>
    </div>
  );
}

/**
 * One edit-mode slot pill. Says so when the title lookup fails, rather than staying "Loading…".
 *
 * THE TOOLTIP FOLLOWS THE LABEL ONCE THE LOOKUP LANDS, and that is the original's behaviour
 * rather than its intent: `openPopup()` set `title` to "Click to edit this document" when it
 * built the pill, and then `setDocPillLabel()` — which writes `el.title = label` — overwrote it
 * with the fetched title a moment later. So the steady state a person actually hovers is the
 * page's title, and the action wording is only what shows while the request is in flight (or
 * when the slot holds a bare URL with no page behind it, where there is nothing to edit and
 * nothing to fetch). Ported as observed, not as the code reads.
 */
function SlotPill({
  docId,
  fallback,
  color,
  docTypes,
  locale,
  onClick,
}: {
  docId: DocId | null;
  fallback: string;
  color: string;
  docTypes: Record<DocId, DocType>;
  locale: string;
  onClick: () => void;
}) {
  const fetched = useServerDocTitle(docId, locale, '(could not load title)');
  return (
    <DocPill
      variant="block"
      label={fetched ?? fallback}
      color={color}
      docId={docId}
      docTypes={docTypes}
      title={fetched ?? (docId ? 'Click to edit this document' : '')}
      style={{ marginTop: 2 }}
      onClick={onClick}
    />
  );
}
